import { createContext, useMemo, useReducer } from "react";

import type { Dispatch, ReactNode } from "react";

interface AuthProviderProps {
  children: ReactNode;
}

interface AuthContextState {
  isAuthenticated: boolean;
  user: User | null;
  isLoading: boolean;
  isInitialized: boolean;
  auth: RefreshTokensAuthData | SignInAuthData | null;
  loginError: string | null;
  challengeAuth: ChangePasswordChallengeAuthenticationResult | null;
  attemptedVoluntellLogin: boolean;
  pubnub: PubnubClientData | null;
  accountLink: AccountLink | null;
  mfaChallenge: MFAChallengeData | null;
}

interface RefreshTokenAction {
  type: "refreshToken";
  data: {
    user: User;
    auth: RefreshTokensAuthData;
    pubnub: PubnubClientData;
    accountLink: AccountLink | null;
  };
}

interface RequireAuthAction {
  type: "requireAuth";
}

interface RequestLoginAction {
  type: "requestLogin";
  data: {
    voluntellLogin: boolean;
  };
}

interface LoginAction {
  type: "login";
  data: {
    user: User;
    auth: SignInAuthData;
    pubnub: PubnubClientData;
    accountLink: AccountLink | null;
  };
}

interface LoginErrorAction {
  type: "loginError";
  data: {
    loginErrorMsg: string | null;
  };
}

interface SetUserAction {
  type: "setUser";
  data: {
    user: User;
  };
}

interface UpdatePubnubData {
  type: "updatePubnubData";
  data: {
    pubnub: PubnubClientData;
  };
}

interface ChangePasswordChallengeAction {
  type: "changePasswordChallenge";
  data: {
    user: User;
    challengeAuth: ChangePasswordChallengeAuthenticationResult;
    isVoluntell: boolean;
    pubnub: PubnubClientData;
    accountLink: AccountLink | null;
  };
}

interface MFAChallengeAction {
  type: "mfaChallenge";
  data: {
    mfaChallenge: MFAChallengeData;
  };
}

interface ClearMFAChallengeAction {
  type: "clearMfaChallenge";
}

type AuthActions =
  | LoginAction
  | LoginErrorAction
  | RefreshTokenAction
  | RequestLoginAction
  | RequireAuthAction
  | SetUserAction
  | ChangePasswordChallengeAction
  | UpdatePubnubData
  | MFAChallengeAction
  | ClearMFAChallengeAction;

interface AuthContextDispatch {
  dispatch: Dispatch<AuthActions>;
}

const initalState: AuthContextState = {
  isAuthenticated: false,
  user: null,
  isLoading: true,
  isInitialized: false,
  auth: null,
  loginError: null,
  challengeAuth: null,
  attemptedVoluntellLogin: false,
  pubnub: null,
  accountLink: null,
  mfaChallenge: null,
};

export const authContext = createContext<
  AuthContextState & AuthContextDispatch
>({
  ...initalState,
  dispatch: () => null,
});

function reducer(
  state: AuthContextState,
  action: AuthActions,
): AuthContextState {
  switch (action.type) {
    case "setUser":
      return {
        ...state,
        user: action.data.user,
      };
    case "requestLogin":
      return {
        ...state,
        isLoading: true,
        attemptedVoluntellLogin: action.data.voluntellLogin,
      };
    case "login":
      return {
        isAuthenticated: true,
        user: action.data.user,
        auth: action.data.auth,
        isLoading: false,
        isInitialized: true,
        challengeAuth: null,
        loginError: null,
        attemptedVoluntellLogin: false,
        pubnub: action.data.pubnub,
        accountLink: action.data.accountLink,
        mfaChallenge: null,
      };
    case "loginError":
      return {
        ...state,
        isLoading: false,
        loginError: action.data.loginErrorMsg,
      };
    case "changePasswordChallenge":
      return {
        isAuthenticated: false,
        user: action.data.user,
        auth: null,
        isLoading: false,
        isInitialized: true,
        loginError: null,
        challengeAuth: action.data.challengeAuth,
        attemptedVoluntellLogin: true,
        pubnub: action.data.pubnub,
        accountLink: action.data.accountLink,
        mfaChallenge: null,
      };
    case "refreshToken":
      return {
        isAuthenticated: true,
        user: action.data.user,
        auth: action.data.auth,
        isLoading: false,
        isInitialized: true,
        loginError: null,
        challengeAuth: null,
        attemptedVoluntellLogin: false,
        pubnub: action.data.pubnub,
        accountLink: action.data.accountLink,
        mfaChallenge: null,
      };
    case "mfaChallenge":
      return {
        ...state,
        mfaChallenge: action.data.mfaChallenge,
        isLoading: false,
      };
    case "clearMfaChallenge":
      return {
        ...state,
        mfaChallenge: null,
        isLoading: false,
      };
    case "requireAuth":
      return {
        ...state,
        isLoading: false,
        isInitialized: true,
      };
    case "updatePubnubData":
      return {
        ...state,
        pubnub: action.data.pubnub,
      };
    default:
      return state;
  }
}

export function AuthProvider(props: AuthProviderProps) {
  const { children } = props;

  const [state, dispatch] = useReducer(reducer, initalState);

  const memoState = useMemo(() => ({ ...state, dispatch }), [state]);

  return (
    <authContext.Provider value={memoState}>{children}</authContext.Provider>
  );
}
